/**
 * coerceSearchMode — B3 guard for per-surface mode validity.
 *
 * Returns ``filters`` with ``searchMode`` downgraded to ``'labels'`` if the
 * current mode is not enabled for the given surface + regions + admin context.
 *
 * Import ``isModeEnabled`` from filterConstants (NOT buildModeOptions from
 * SearchFilters) to avoid a circular dependency:
 *   SearchFilters imports filterConstants
 *   SearchFilters is a React component
 *   coerceSearchMode is a plain util used by SearchPage/ResearchPanel
 * If coerceSearchMode imported from SearchFilters, any component importing
 * both would form a SearchFilters → coerceSearchMode → SearchFilters cycle.
 */
import type { SearchFilterOptions } from '../types';
import { isModeEnabled } from '../constants/filterConstants';
import type { Surface } from '../constants/filterConstants';

/**
 * Guard that enforces per-surface mode validity.
 *
 * Call on:
 *  - region toggle (handleRegionToggle)
 *  - URL-sync restore (kwSearchMode / searchMode / researchSearchMode)
 *  - submit (handleHybridSearch, ResearchPanel POST, keyword submit)
 *
 * ``hasLabelNegotiations`` (tenant owns ≥1 draft) additionally downgrades the
 * two Label Negotiations modes. This is what recovers a bookmarked or
 * saved-search URL: without it, a tenant with no drafts restoring
 * ``?mode=label_negotiations`` would land on an ACTIVE-but-greyed pill driving
 * a search that can only return nothing. Defaults true for the same reason as
 * ``isModeEnabled`` — see its docstring.
 *
 * Always returns a new object (spread), never mutates ``filters``.
 */
export function coerceSearchMode(
  filters: SearchFilterOptions,
  surface: Surface,
  isAdmin: boolean,
  hasLabelNegotiations: boolean = true,
): SearchFilterOptions {
  if (isModeEnabled(filters.searchMode, surface, filters.regions, isAdmin, hasLabelNegotiations)) {
    return { ...filters };
  }
  return { ...filters, searchMode: 'labels' };
}
